'use client';

import React, { useEffect } from 'react';
import type { UseFormReturnType } from '@mantine/form';
import FormControl from '@dr-pam/common-components/Components/Form/FormControl';
import DropdownSelect from '@dr-pam/common-components/Components/Form/DropdownSelect';
import type { NewsletterSignupFormValues } from './newsletterSignupTypes';
import type { AudienceType, ParentInterest, ProfessionalType } from './newsletterSignupOptions';
import { AUDIENCE_TYPE_OPTIONS, PARENT_INTEREST_OPTIONS, PROFESSIONAL_TYPE_OPTIONS } from './newsletterSignupOptions';

export type NewsletterSignupAudienceFieldsProps = {
	form: UseFormReturnType<NewsletterSignupFormValues>;
	disabled?: boolean;
};

export default function NewsletterSignupAudienceFields(props: NewsletterSignupAudienceFieldsProps) {
	const { form, disabled } = props;
	const { audienceType, parentInterest, professionalType } = form.values;
	const isParent = audienceType === 'parent';
	const isProfessional = audienceType === 'professional';
	const hasAudienceSelected = audienceType !== '';

	useEffect(() => {
		if (isParent) {
			if (professionalType !== '') {
				form.setFieldValue('professionalType', '');
			}
			return;
		}
		if (isProfessional) {
			if (parentInterest !== '') {
				form.setFieldValue('parentInterest', '');
			}
			return;
		}
		if (parentInterest !== '') {
			form.setFieldValue('parentInterest', '');
		}
		if (professionalType !== '') {
			form.setFieldValue('professionalType', '');
		}
	}, [audienceType, isParent, isProfessional, parentInterest, professionalType, form]);

	const showParentInterestField = !hasAudienceSelected || isParent;

	return (
		<>
			<FormControl name="audienceType" disabled={disabled} required>
				<DropdownSelect<'' | AudienceType>
					options={AUDIENCE_TYPE_OPTIONS}
					value={audienceType}
					onChange={(nextAudienceType) => form.setFieldValue('audienceType', nextAudienceType)}
					disabled={disabled}
					iconSrc="/images/icons/dark-icons/ArrowDown.svg"
				/>
			</FormControl>

			{showParentInterestField ? (
				<FormControl
					name="parentInterest"
					disabled={disabled || !isParent}
					required={isParent}
					className={!isParent ? 'is-disabled' : undefined}
				>
					<DropdownSelect<'' | ParentInterest>
						options={PARENT_INTEREST_OPTIONS}
						value={parentInterest}
						onChange={(nextParentInterest) => form.setFieldValue('parentInterest', nextParentInterest)}
						disabled={disabled || !isParent}
						iconSrc="/images/icons/dark-icons/ArrowDown.svg"
					/>
				</FormControl>
			) : (
				<FormControl name="professionalType" disabled={disabled || !isProfessional} required={isProfessional}>
					<DropdownSelect<'' | ProfessionalType>
						options={PROFESSIONAL_TYPE_OPTIONS}
						value={professionalType}
						onChange={(nextProfessionalType) =>
							form.setFieldValue('professionalType', nextProfessionalType)
						}
						disabled={disabled || !isProfessional}
						iconSrc="/images/icons/dark-icons/ArrowDown.svg"
					/>
				</FormControl>
			)}
		</>
	);
}
